// @ts-nocheck
import { type App, type InjectionKey, inject } from 'vue';

export interface GoogleTagConfiguration {
  id: string,
  name: 'dataLayer' | string;
}

export const GoogleTagManagerInjectionKey = Symbol('GoogleTagManager') as InjectionKey<GoogleTagManager>;

const CONSENT_MODE_DEFAULTS = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'denied',
};

export class GoogleTagManager {
  private layers: Set<string> = new Set();

  constructor(tags: GoogleTagConfiguration[]) {
    this.createDataLayer('dataLayer');

    if (typeof window !== 'undefined') {
      tags.forEach(tag => this.start(tag));
    }
  }

  // need to push data as an argument object
  public push(event: string, action: string, data: Object);
  public push(event: Array<Object>);
  public push(event: Array);
  public push(event: Object);
  public push() {
    if (typeof window !== 'undefined') {
      this.layers.forEach((name) => {
        if (arguments[0]?.ecommerce !== undefined) {
          window[name].push({ 'ecommerce': null });
          window[name].push(arguments[0]);
        } else {
          window[name].push(arguments);
        }
      });
    }
  }

  private start(config: GoogleTagConfiguration) {
    let { id, name } = config;

    this.createDataLayer(name);

    // need to push data as an argument object
    const gtag = function () {
      window[name].push(arguments);
    };

    gtag('consent', 'default', CONSENT_MODE_DEFAULTS);
    window[name].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    let element = document.getElementsByTagName('script')[0]!;
    let script = document.createElement('script');

    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + id + (name != 'dataLayer' ? '&l' + name : '');
    script.async = true;

    element.parentNode!.insertBefore(script, element);
  }

  private createDataLayer(name: string) {
    if (typeof window !== 'undefined') {
      this.layers.add(name);

      window[name] = window[name] || [];
    }
  }
}

export function createGoogleTagManager(tags: GoogleTagConfiguration[]) {
  return {
    install(app: App) {
      const manager = new GoogleTagManager(tags);

      app.provide(GoogleTagManagerInjectionKey, manager);

      app.config.globalProperties.$gtag = manager.push.bind(manager);
    },
  };
}

export function useGoogleTagManager() {
  return inject(GoogleTagManagerInjectionKey)!;
}

/**
 * @deprecated Use `useGoogleTagManager()` instead.
 */
export function gtag(...args: any[]) {
  return useGoogleTagManager().push(...args);
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $gtag: typeof GoogleTagManager.prototype.push;
  }
}
